import { isAudioStation } from '@app/core/stations/helpers';
import { useStationsFinder } from '@app/core/stations/hooks/useStations';
import { webApi } from '@mi-front/api-clients';
import { useMemo } from 'react';

export const SOCIAL_EXPORT_FRAGMENT_SOURCE = 'fragment';

export interface SocialExportAvailableSource {
  value: string;
  sourceId: string;
  sourceType: webApi.CreateSocialUploadDtoSourceTypeEnumKey;
}

interface UseAvailableSocialExportSourcesParams {
  fragmentId: string;
  stationId?: string;
}

/**
 * Resolves publishable sources for social export for a fragment.
 * Audio stations only allow rendered video-edition projects.
 */
export function useAvailableSocialExportSources({
  fragmentId,
  stationId,
}: UseAvailableSocialExportSourcesParams) {
  const findStation = useStationsFinder();
  const station = stationId ? findStation(stationId) : null;
  const isFragmentSourceAllowed = station ? !isAudioStation(station) : true;

  const projectsListQuery = webApi.useListProjectsVideoEditorProjects(
    { fragmentId },
    { query: { enabled: Boolean(fragmentId) } },
  );

  const projects = projectsListQuery.data ?? [];
  const projectsWithRender = useMemo(
    () => projects.filter((project) => project.renderJob?.status === 'COMPLETED'),
    [projects],
  );

  const availableSources = useMemo(() => {
    const fragmentSource: SocialExportAvailableSource[] = isFragmentSourceAllowed
      ? [
          {
            value: SOCIAL_EXPORT_FRAGMENT_SOURCE,
            sourceId: fragmentId,
            sourceType: webApi.CreateSocialUploadDtoSourceTypeEnum.FRAGMENT,
          },
        ]
      : [];
    const projectSources = projectsWithRender.map(
      (project): SocialExportAvailableSource => ({
        value: project.id,
        sourceId: project.id,
        sourceType: webApi.CreateSocialUploadDtoSourceTypeEnum.VIDEO_EDITOR_PROJECT,
      }),
    );

    return [...fragmentSource, ...projectSources];
  }, [fragmentId, isFragmentSourceAllowed, projectsWithRender]);

  return {
    projectsListQuery,
    projects,
    projectsWithRender,
    isFragmentSourceAllowed,
    availableSources,
    hasAvailableSources: availableSources.length > 0,
  };
}
